import { AdSlot } from "./display-ads/AdSlot";
import type { PublicAdsConfig } from "@serendiptv/contracts";
import { StationChat } from "./StationChat";
import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from "react";
import type { SelectionResponse, StationSummary } from "./types";
import { programContext } from "./lib/metadata";

type Props = {
  ads: PublicAdsConfig;
  adEligible: boolean;
  hidden: boolean;
  channel: number;
  stations: StationSummary[];
  title: string;
  subtitle: string | null;
  item: SelectionResponse["item"] | null;
  watchingNow: number | null;
  stationWatchingNow: number | null;
  titleRef: RefObject<HTMLButtonElement | null>;
  onTune: (channel: number, direction: "up" | "down") => void;
  onInfo: (event: MouseEvent<HTMLElement>) => void;
  onGuide: (event: MouseEvent<HTMLElement>) => void;
  volumeControl: ReactNode;
  volume: number;
  muted: boolean;
  chatEnabled: boolean;
};

export function TelevisionPanel({ ads, adEligible, hidden, channel, stations, title, subtitle, item, watchingNow, stationWatchingNow, titleRef, onTune, onInfo, onGuide, volumeControl, volume, muted, chatEnabled }: Props) {
  const index = stations.findIndex((station) => station.channel === channel);
  const canTune = stations.length > 1;
  const step = (direction: "up" | "down") => {
    if (!canTune) return;
    const nextIndex = index < 0 ? (direction === "up" ? 0 : stations.length - 1)
      : (index + (direction === "up" ? 1 : -1) + stations.length) % stations.length;
    onTune(stations[nextIndex]!.channel, direction);
  };
  const handleKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (["ArrowRight", "ArrowUp", "PageUp"].includes(event.key)) {
      event.preventDefault(); step("up");
    } else if (["ArrowLeft", "ArrowDown", "PageDown"].includes(event.key)) {
      event.preventDefault(); step("down");
    }
  };
  const context = programContext(item);
  return (
    <aside hidden={hidden} className="control-spine television-panel" aria-label="Television controls and program information">
      <div className="station-tuner">
        <div className="station-display">
          <span className="panel-label">CHANNEL</span>
          <output className="station-channel" aria-label="Current channel">{channel}</output>
          <span className="station-name">{stations[index]?.name || (stations.length ? `Channel ${channel}` : "NO STATIONS")}</span>
        </div>
        <div className="channel-rocker" role="group" aria-label="Change station">
          <button type="button" tabIndex={0} onClick={() => step("down")} onKeyDown={handleKey} disabled={!canTune} aria-label="Previous station">CH <span aria-hidden="true">−</span></button>
          <button type="button" tabIndex={0} onClick={() => step("up")} onKeyDown={handleKey} disabled={!canTune} aria-label="Next station">CH <span aria-hidden="true">+</span></button>
        </div>
      </div>
      <div className="now-playing-module">
        <button ref={titleRef} className="program-plate" type="button" tabIndex={0} onClick={onInfo} aria-label={`Program information for ${title}${subtitle ? `: ${subtitle}` : ""}`}>
          <span className="panel-label">NOW PLAYING</span>
          <strong className="program-title">{title}</strong>
          {subtitle ? <span className="program-subtitle">{subtitle}</span> : null}
        </button>
        {context ? <p className="program-context" aria-label={`Program context: ${context}`}>{context}</p> : null}
      </div>
      <AdSlot placement="cabinet" config={ads} eligible={adEligible} />
      <StationChat channel={channel} stationName={stations[index]?.name ?? `Channel ${channel}`} available={index >= 0} volume={volume} muted={muted} enabled={chatEnabled}>
      <div className="audience-readout">
        <span aria-label={stationWatchingNow === null ? "Station viewer count unavailable" : `${stationWatchingNow} watching this station`}><strong>{stationWatchingNow === null ? "—" : stationWatchingNow.toLocaleString()}</strong> on CH {channel}</span>
        <span aria-label={watchingNow === null ? "SerendipTV viewer count unavailable" : `${watchingNow} watching now across SerendipTV`}><strong>{watchingNow === null ? "—" : watchingNow.toLocaleString()}</strong> {watchingNow === null ? "count unavailable" : "across SerendipTV"}</span>
      </div>
      </StationChat>
      <div className="panel-actions">
        <button className="panel-action" type="button" tabIndex={0} onClick={onInfo} aria-label="Program information">INFO</button>
        <button className="panel-action" type="button" tabIndex={0} onClick={onGuide} aria-label="Open broadcast guide">GUIDE</button>
        {volumeControl}
      </div>
    </aside>
  );
}
